import { Button, Input, Modal, Switch } from 'antd';
import { observer } from 'mobx-react';
import { type ChangeEvent, useEffect, useRef } from 'react';
import styled from 'styled-components';

import store from 'stores/RootStore';
import { removeSpecialCharacters } from 'utils/String';

const TEMPLATE_UPLOAD_MODAL_TITLE = 'Upload Template';

const PropertyModal = styled(Modal)`
    .last-form-item {
        margin-bottom: 0px;
    }
`;

const SwitchContainer = styled.span`
    width: 151px;
    float: left;
    display: flex;
    justify-content: space-between;
    align-items: center;
    height: 30px;
`;

const FileInput = styled.span`
    width: 100%;
    display: flex;
    justify-content: left;
    flex-wrap: nowrap;
    margin-bottom: 10px;
`;

const FileNameInput = styled(Input)`
    flex-grow: 1;
`;

const UploadElement = styled.input`
    display: none;
    width: 100px;
    border: 1px solid #ddd;
    border-radius: 5px;
    background: white;
`;

function UploadTemplateModal() {
    const store_creative = store.creative;
    const uploadManager = store_creative?.templates.uploadManager;
    const modalVisible = uploadManager?.modalVisible;
    const uploadRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        if (!modalVisible && uploadRef.current) {
            uploadRef.current.value = '';
        }
    }, [modalVisible]);

    if (!store_creative || !uploadManager) return null;

    const handleNameChange = (e: ChangeEvent<HTMLInputElement>) => {
        uploadManager.templateName = removeSpecialCharacters(e.target.value);
    };

    // `hideModalAndReset` is genuinely async - it waits out the close
    // animation before resetting - unlike AssetUploadManager's `hide`.
    const handleCancel = () => {
        void uploadManager.hideModalAndReset();
    };

    const enableConfirm = !!uploadManager.file && !!uploadManager.templateName;

    return (
        <PropertyModal
            title={TEMPLATE_UPLOAD_MODAL_TITLE}
            open={uploadManager.modalVisible}
            confirmLoading={uploadManager.loading}
            onCancel={handleCancel}
            footer={
                <>
                    {uploadManager.isUpdate && (
                        <SwitchContainer>
                            Replace Assets
                            <Switch
                                checked={uploadManager.replaceAssets}
                                onChange={(checked) => {
                                    uploadManager.replaceAssets = checked;
                                }}
                            />
                        </SwitchContainer>
                    )}
                    <Button onClick={handleCancel}>Cancel</Button>
                    <Button
                        disabled={!enableConfirm}
                        type="primary"
                        loading={uploadManager.loading}
                        onClick={() => uploadManager.upload()}
                    >
                        {uploadManager.isUpdate ? 'Update' : 'Add'}
                    </Button>
                </>
            }
        >
            <UploadElement
                type="file"
                className="file-select"
                accept=".zip"
                ref={uploadRef}
                onChange={(e) => {
                    if (e.target.files && e.target.files.length === 1) {
                        uploadManager.file = e.target.files[0];
                    }
                }}
            />
            <FileInput>
                <FileNameInput
                    placeholder="Select a file from your computer"
                    disabled
                    value={uploadManager.fileName}
                />
                <Button
                    style={{ marginLeft: '5px' }}
                    onClick={() => {
                        uploadRef.current?.click();
                    }}
                >
                    Browse
                </Button>
            </FileInput>
            <Input
                placeholder="Type a template name"
                value={uploadManager.templateName}
                onChange={handleNameChange}
            />
        </PropertyModal>
    );
}

export default observer(UploadTemplateModal);
